import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateDisbursementMutation } from '../../../api/DisbursementsApi';
import { usePolicy } from '../../../api/PolicyApi';
import {
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { usePolicyContext } from '../../../context/policy';
import { CurrencyCode } from '../../../types/currencyCode';
import { FastForm } from '../../fastForm';
import {
	DISBURSEMENT_SPECIAL_FIELDS,
	FORM_NAME,
} from '../../fastForm/data/constants';
import {
	CurrencyField,
	PolicyholderLookupField,
	PolicyLookupField,
	SelectField,
} from '../../fastForm/fields';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import getDisbursementForm, {
	DisbursementFormValues,
} from '../../fastForm/formBuilders/getDisbursementForm';
import { disbursementCreateBuilder } from '../../fastForm/updateBuilders/disbursementUpdateBuilder';
import { getValidationSchemaWithoutResolver } from '../../fastForm/utils/getValidationSchema';
import ActiveLastBreadcrumb from '../components/Breadcrumbs';
import DisbursementStateChip from '../components/DisbursementStateChip';
import {
	CREATE_DISBURSEMENT_PAGE__CANCEL,
	CREATE_DISBURSEMENT_PAGE__SUBMIT,
	CREATE_DISBURSEMENT_PAGE__TITLE,
} from '../data/constants';
import { disbursementSchema } from '../lib/disbursementSchema';

const CreateDisbursementPage = () => {
	const authData = useAuthContext();
	const config = useConfigContext();
	const policyData = usePolicyContext();
	const {
		policyholderId: policyholderLocatorFromParams,
		policyId: policyLocatorFromParams,
	} = useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];
	const timezone = authData.auth?.timezone;
	const { data: policyResponse } = usePolicy(
		policyLocatorFromParams,
		authData?.auth?.authorizationToken,
	);
	const cashBalanceLocator = policyResponse?.cashBalance?.locator;
	const cashBalanceAmount = policyResponse?.cashBalance?.amount;
	const defaultCurrency = policyResponse?.currency || 'USD';
	const authToken = authData.auth?.authorizationToken;
	const form = getDisbursementForm({
		fields: config.cashManagementConfig?.configuration?.disbursement?.fields,
		fieldValues: {},
		mediaByLocator: {},
		timezone,
		multiCurrencyEnabled,
	});

	const defaultValues = getDefaultPolicyValues(form, authData);

	const methods = useForm<DisbursementFormValues>({
		defaultValues: {
			disbursement: {
				...(defaultValues.disbursement as object),
				policyholderLocator: '',
				policyLocator: '',
				amountCurrency: defaultCurrency,
				amount: cashBalanceAmount || '',
			},
		},
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			form &&
			yupResolver(
				getValidationSchemaWithoutResolver(form).concat(disbursementSchema()),
			),
	});

	const { handleSubmit, formState, control, watch, setValue } = methods;

	useEffect(() => {
		if (policyholderLocatorFromParams) {
			setValue(
				'disbursement.policyholderLocator',
				policyholderLocatorFromParams,
				{
					shouldDirty: true,
				},
			);
		}

		if (policyLocatorFromParams) {
			setValue(
				'disbursement.policyholderLocator',
				policyResponse?.policyholderLocator,
				{ shouldDirty: true },
			);
			setValue('disbursement.policyLocator', policyLocatorFromParams, {
				shouldDirty: true,
			});
		}
	}, [
		policyholderLocatorFromParams,
		policyLocatorFromParams,
		policyResponse,
		setValue,
	]);

	const currency = watch(
		`${FORM_NAME.disbursement as 'disbursement'}.${
			DISBURSEMENT_SPECIAL_FIELDS.amountCurrency
		}`,
	) as CurrencyCode;

	const dirtyFields = formState.dirtyFields;

	const create = useCreateDisbursementMutation({
		authToken,
		queryClient,
	});

	const onSubmit: SubmitHandler<DisbursementFormValues> = async (
		formValues,
	) => {
		const updateRequest = disbursementCreateBuilder(
			dirtyFields,
			formValues,
			form,
			FORM_NAME.disbursement,
			timezone,
			cashBalanceLocator,
		);

		const response = await create.mutateAsync(updateRequest as any);
		if (response.data.locator) {
			navigate(`../${response.data.locator}/edit`, { relative: 'path' });
		}
	};

	const disableBreadcrumb =
		!!policyholderLocatorFromParams || !!policyLocatorFromParams;

	return (
		<FormProvider {...methods}>
			<Box component='form' noValidate onSubmit={handleSubmit(onSubmit)}>
				<Stack
					spacing={2}
					direction='row'
					justifyContent='space-between'
					alignItems='flex-start'
				>
					{!disableBreadcrumb && <ActiveLastBreadcrumb isNew />}
					<Stack spacing={2} direction='row' alignItems='center' pb={4}>
						<Typography variant='h3'>
							{CREATE_DISBURSEMENT_PAGE__TITLE}
						</Typography>
						<DisbursementStateChip state='draft' />
					</Stack>
					<Stack spacing={2} direction='row'>
						<Button type='button' onClick={() => navigate(-1)}>
							{CREATE_DISBURSEMENT_PAGE__CANCEL}
						</Button>
						<Button
							variant='contained'
							type='submit'
							disabled={create.isLoading}
						>
							{CREATE_DISBURSEMENT_PAGE__SUBMIT}
						</Button>
					</Stack>
				</Stack>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<SelectField<DisbursementFormValues>
							order={0}
							name={`${FORM_NAME.disbursement as 'disbursement'}.${
								DISBURSEMENT_SPECIAL_FIELDS.amountCurrency
							}`}
							title='Currency'
							disabled
							control={control}
							type='select'
							values={[]}
						/>
					</Grid>
					<Grid item xs={6}>
						<CurrencyField
							order={1}
							name={`${FORM_NAME.disbursement as 'disbursement'}.${
								DISBURSEMENT_SPECIAL_FIELDS.amount
							}`}
							title='Amount'
							currency={currency}
							disabled={create.isLoading}
							control={control}
							type='select'
							values={[]}
						/>
					</Grid>
					<Grid item xs={6}>
						<PolicyholderLookupField<DisbursementFormValues>
							policyholderKey={`${FORM_NAME.disbursement}.${DISBURSEMENT_SPECIAL_FIELDS.policyholderLocator}`}
							order={2}
							name={`${FORM_NAME.disbursement as 'disbursement'}.${
								DISBURSEMENT_SPECIAL_FIELDS.policyholderLocator
							}`}
							title='Policyholder'
							disabled={
								create.isLoading ||
								!!policyholderLocatorFromParams ||
								!!policyLocatorFromParams
							}
							control={control}
							type='text'
							values={[]}
						/>
					</Grid>
					<Grid item xs={6}>
						<PolicyLookupField<DisbursementFormValues>
							policyKey={`${FORM_NAME.disbursement}.${DISBURSEMENT_SPECIAL_FIELDS.policyLocator}`}
							policyholderKey={`${FORM_NAME.disbursement}.${DISBURSEMENT_SPECIAL_FIELDS.policyholderLocator}`}
							order={3}
							name={`${FORM_NAME.disbursement as 'disbursement'}.${
								DISBURSEMENT_SPECIAL_FIELDS.policyLocator
							}`}
							title='Policy'
							disabled={create.isLoading || !!policyLocatorFromParams}
							control={control}
							type='text'
							values={[]}
						/>
					</Grid>
					<FastForm
						formName={FORM_NAME.disbursement}
						form={form}
						productName={policyData?.productConfig?.name}
						configVersion=''
						authData={authData}
						disabled={create.isLoading}
					/>
				</Grid>
				<DevTool control={control} />
			</Box>
		</FormProvider>
	);
};

export default CreateDisbursementPage;
